import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PageHeader } from "@/components/platform/platformUi";

export default function PlatformSettings() {
    const [form, setForm] = useState(null);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        api.get("/platform/settings")
            .then((r) => setForm(r.data))
            .catch(() => toast.error("Could not load settings"));
    }, []);

    const save = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            const r = await api.put("/platform/settings", form);
            setForm(r.data);
            toast.success("Settings saved");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Save failed");
        } finally {
            setBusy(false);
        }
    };

    if (!form) {
        return (
            <div className="flex min-h-[40vh] items-center justify-center p-6">
                <p className="font-serif text-xl text-[#8A8178]">Loading settings…</p>
            </div>
        );
    }

    return (
        <div className="space-y-6 p-6 lg:p-8" data-testid="platform-settings-page">
            <PageHeader
                eyebrow="Settings"
                title="Platform settings"
                subtitle="Global SaaS configuration — Razorpay keys stay on the server (.env)"
            />

            <form onSubmit={save} className="max-w-2xl space-y-5 rounded-xl border border-[#E8E4DE] bg-white p-6 shadow-[0_4px_22px_rgba(45,41,38,0.05)]">
                <div className="space-y-1">
                    <Label className="text-[11px] font-bold uppercase text-[#3D2817]">Platform name</Label>
                    <Input value={form.platform_name || ""} onChange={(e) => setForm((f) => ({ ...f, platform_name: e.target.value }))} />
                </div>
                <div className="space-y-1">
                    <Label className="text-[11px] font-bold uppercase text-[#3D2817]">Support email</Label>
                    <Input type="email" value={form.support_email || ""} onChange={(e) => setForm((f) => ({ ...f, support_email: e.target.value }))} />
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-1">
                        <Label className="text-[11px] font-bold uppercase text-[#3D2817]">Default trial days</Label>
                        <Input type="number" min={0} max={90} value={form.default_trial_days ?? 14} onChange={(e) => setForm((f) => ({ ...f, default_trial_days: Number(e.target.value) }))} />
                    </div>
                    <div className="space-y-1">
                        <Label className="text-[11px] font-bold uppercase text-[#3D2817]">Billing grace days</Label>
                        <Input type="number" min={0} max={30} value={form.billing_grace_days ?? 3} onChange={(e) => setForm((f) => ({ ...f, billing_grace_days: Number(e.target.value) }))} />
                    </div>
                </div>
                <div className="space-y-1">
                    <Label className="text-[11px] font-bold uppercase text-[#3D2817]">Currency</Label>
                    <Input value={form.currency || "INR"} onChange={(e) => setForm((f) => ({ ...f, currency: e.target.value }))} maxLength={3} />
                </div>
                <div className="flex flex-col gap-3">
                    <label className="flex items-center gap-2 text-sm font-medium text-[#3D2817]">
                        <input type="checkbox" checked={!!form.razorpay_enabled} onChange={(e) => setForm((f) => ({ ...f, razorpay_enabled: e.target.checked }))} />
                        Razorpay enabled for salon subscription checkout
                    </label>
                    <label className="flex items-center gap-2 text-sm font-medium text-[#3D2817]">
                        <input type="checkbox" checked={!!form.maintenance_mode} onChange={(e) => setForm((f) => ({ ...f, maintenance_mode: e.target.checked }))} />
                        Maintenance mode (block new signups)
                    </label>
                </div>
                <p className="rounded-lg bg-[#FAF9F6] px-4 py-3 text-xs text-[#8A8178]">
                    Payment gateway credentials (<code>RAZORPAY_KEY_ID</code>, <code>RAZORPAY_KEY_SECRET</code>) are configured in the backend environment only — salons pay through Razorpay on their billing page; you reconcile here in Billing.
                </p>
                <Button type="submit" disabled={busy} className="bg-[#4A0E17] text-white hover:bg-[#3a0b12]">
                    Save settings
                </Button>
            </form>
        </div>
    );
}
